import { useState, useEffect, useMemo } from 'react'
import {
  Car,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ArrowRight,
  MapPin,
  Flag,
  Play,
  Square,
  Download
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { format, startOfMonth, endOfMonth, addMonths, subMonths, startOfYear, endOfYear } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { useAuth } from '../../lib/auth'
import { AddressSearch } from '../../components/AddressSearch'
import {
  useDrivingLogVehicles,
  useCreateDrivingLogVehicle,
  useUpdateDrivingLogVehicle,
  useDrivingLogEntries,
  useLastDrivingLogEntry,
  useOpenDrivingLogTrip,
  useCreateDrivingLogEntry,
  useUpdateDrivingLogEntry,
  useDeleteDrivingLogEntry,
  useAssignmentsForDate,
} from '../../hooks/useDrivingLog'
import { drivingLogEntriesService } from '../../services/drivingLog'
import { supabase } from '../../lib/supabase'
import type { DrivingLogEntry } from '../../types/drivingLog'

type TripType = 'assignment' | 'other'

const today = () => format(new Date(), 'yyyy-MM-dd')

export function DrivingLog() {
  const { profile } = useAuth()

  // Vehicle state
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('')
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null)
  const [vehicleForm, setVehicleForm] = useState({ name: '', registration_number: '' })

  // Period state
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const dateFrom = format(startOfMonth(currentMonth), 'yyyy-MM-dd')
  const dateTo = format(endOfMonth(currentMonth), 'yyyy-MM-dd')

  // Start trip modal state
  const [showStartModal, setShowStartModal] = useState(false)
  const [tripType, setTripType] = useState<TripType>('assignment')
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<string[]>([])
  const [startForm, setStartForm] = useState({
    date: today(),
    from_address: '',
    to_address: '',
    odometer_start: 0,
    purpose: '',
    notes: '',
  })

  // Finish trip modal state
  const [showFinishModal, setShowFinishModal] = useState(false)
  const [finishOdometer, setFinishOdometer] = useState(0)

  // Edit modal state (for completed entries)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<DrivingLogEntry | null>(null)
  const [editForm, setEditForm] = useState({
    date: '',
    from_address: '',
    to_address: '',
    odometer_start: 0,
    odometer_end: 0 as number | null,
    purpose: '',
    notes: '',
  })

  // Return trip offer
  const [showReturnOffer, setShowReturnOffer] = useState(false)
  const [completedTrip, setCompletedTrip] = useState<DrivingLogEntry | null>(null)

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFrom, setExportFrom] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'))
  const [exportTo, setExportTo] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'))
  const [exporting, setExporting] = useState(false)

  // Admin address (fetched from subcontractors table)
  const [adminAddress, setAdminAddress] = useState('')

  // Queries
  const { data: vehicles = [], isLoading: loadingVehicles } = useDrivingLogVehicles()
  const { data: entries = [], isLoading: loadingEntries } = useDrivingLogEntries(
    selectedVehicleId, dateFrom, dateTo
  )
  const { data: lastEntry } = useLastDrivingLogEntry(selectedVehicleId)
  const { data: openTrip } = useOpenDrivingLogTrip(selectedVehicleId)
  const { data: assignmentsForDate = [] } = useAssignmentsForDate(startForm.date)

  // Mutations
  const createVehicle = useCreateDrivingLogVehicle()
  const updateVehicle = useUpdateDrivingLogVehicle()
  const createEntry = useCreateDrivingLogEntry()
  const updateEntry = useUpdateDrivingLogEntry()
  const deleteEntry = useDeleteDrivingLogEntry()

  // Auto-select first vehicle
  useEffect(() => {
    if (vehicles.length > 0 && !selectedVehicleId) {
      setSelectedVehicleId(vehicles[0].id)
    }
  }, [vehicles, selectedVehicleId])

  // Auto-select assignment if only one exists for the date
  useEffect(() => {
    if (showStartModal && tripType === 'assignment' && assignmentsForDate.length === 1 && selectedAssignmentIds.length === 0) {
      const a = assignmentsForDate[0]
      setSelectedAssignmentIds([a.id])
      setStartForm(prev => ({
        ...prev,
        to_address: a.address || prev.to_address,
        purpose: a.title || prev.purpose,
      }))
    }
  }, [showStartModal, tripType, assignmentsForDate, selectedAssignmentIds.length])

  // Fetch admin address from subcontractors table
  useEffect(() => {
    if (profile?.id) {
      supabase
        .from('subcontractors')
        .select('address_street, address_city')
        .eq('id', profile.id)
        .single()
        .then(({ data }) => {
          if (data?.address_street) {
            setAdminAddress(
              data.address_city
                ? `${data.address_street}, ${data.address_city}`
                : data.address_street
            )
          }
        })
    }
  }, [profile?.id])

  // Summary
  const totalKm = useMemo(() => {
    return entries.reduce((sum, e) => sum + (e.km || 0), 0)
  }, [entries])

  // ============================================
  // Vehicle handlers
  // ============================================

  const openVehicleModal = (vehicleId?: string) => {
    if (vehicleId) {
      const v = vehicles.find(v => v.id === vehicleId)
      if (v) {
        setEditingVehicleId(vehicleId)
        setVehicleForm({ name: v.name, registration_number: v.registration_number })
      }
    } else {
      setEditingVehicleId(null)
      setVehicleForm({ name: '', registration_number: '' })
    }
    setShowVehicleModal(true)
  }

  const handleSaveVehicle = async () => {
    if (!vehicleForm.name.trim() || !vehicleForm.registration_number.trim()) {
      toast.error('Fyll i alla fält')
      return
    }

    try {
      if (editingVehicleId) {
        await updateVehicle.mutateAsync({
          id: editingVehicleId,
          updates: {
            name: vehicleForm.name.trim(),
            registration_number: vehicleForm.registration_number.trim().toUpperCase(),
          }
        })
        toast.success('Fordon uppdaterat')
      } else {
        const created = await createVehicle.mutateAsync({
          name: vehicleForm.name.trim(),
          registration_number: vehicleForm.registration_number.trim().toUpperCase(),
          created_by: profile?.id || undefined,
        })
        setSelectedVehicleId(created.id)
        toast.success('Fordon skapat')
      }
      setShowVehicleModal(false)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Okänt fel'
      if (msg.includes('duplicate') || msg.includes('unique')) {
        toast.error('Registreringsnumret finns redan')
      } else {
        toast.error('Kunde inte spara fordon')
      }
    }
  }

  const handleToggleVehicleActive = async (vehicleId: string, isActive: boolean) => {
    try {
      await updateVehicle.mutateAsync({
        id: vehicleId,
        updates: { is_active: !isActive }
      })
      toast.success(isActive ? 'Fordon inaktiverat' : 'Fordon aktiverat')
    } catch {
      toast.error('Kunde inte uppdatera fordon')
    }
  }

  // ============================================
  // Start trip
  // ============================================

  const openStartModal = () => {
    setTripType('assignment')
    setSelectedAssignmentIds([])

    const fromAddr = lastEntry?.to_address || adminAddress || ''
    const odometerStart = lastEntry?.odometer_end || 0

    setStartForm({
      date: today(),
      from_address: fromAddr,
      to_address: '',
      odometer_start: odometerStart,
      purpose: '',
      notes: '',
    })
    setShowStartModal(true)
  }

  const handleAssignmentToggle = (assignmentId: string) => {
    setSelectedAssignmentIds(prev => {
      const next = prev.includes(assignmentId)
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]

      const selected = assignmentsForDate.filter(a => next.includes(a.id))
      const combinedPurpose = selected.map(a => a.title).join(', ')
      const combinedAddresses = selected.map(a => a.address).filter(Boolean).join(', ')

      setStartForm(prev => ({
        ...prev,
        to_address: combinedAddresses || prev.to_address,
        purpose: combinedPurpose || prev.purpose,
      }))

      return next
    })
  }

  const handleStartTrip = async () => {
    if (!startForm.from_address.trim() || !startForm.to_address.trim()) {
      toast.error('Fyll i från- och till-adress')
      return
    }
    if (startForm.odometer_start === 0) {
      toast.error('Fyll i mätarställning vid start')
      return
    }

    try {
      await createEntry.mutateAsync({
        vehicle_id: selectedVehicleId,
        driver_id: profile?.id || null,
        date: startForm.date,
        odometer_start: startForm.odometer_start,
        odometer_end: null,
        from_address: startForm.from_address.trim(),
        to_address: startForm.to_address.trim(),
        purpose: startForm.purpose?.trim() || null,
        assignment_id: tripType === 'assignment' && selectedAssignmentIds.length > 0 ? selectedAssignmentIds[0] : null,
        notes: startForm.notes?.trim() || null,
      })
      toast.success('Resa startad – fyll i mätare slut när du är framme')
      setShowStartModal(false)
    } catch {
      toast.error('Kunde inte starta resa')
    }
  }

  // ============================================
  // Finish trip
  // ============================================

  const openFinishModal = () => {
    setFinishOdometer(0)
    setShowFinishModal(true)
  }

  const handleFinishTrip = async () => {
    if (!openTrip) return
    if (finishOdometer === 0) {
      toast.error('Fyll i mätarställning')
      return
    }
    if (finishOdometer < openTrip.odometer_start) {
      toast.error('Mätare slut kan inte vara mindre än mätare start')
      return
    }

    try {
      const updated = await updateEntry.mutateAsync({
        id: openTrip.id,
        vehicleId: openTrip.vehicle_id,
        updates: { odometer_end: finishOdometer }
      })
      toast.success(`Resa avslutad – ${finishOdometer - openTrip.odometer_start} km`)
      setShowFinishModal(false)

      // Offer return trip
      if (adminAddress && updated.to_address !== adminAddress) {
        setCompletedTrip(updated)
        setShowReturnOffer(true)
      }
    } catch {
      toast.error('Kunde inte avsluta resa')
    }
  }

  // ============================================
  // Return trip
  // ============================================

  const handleStartReturnTrip = () => {
    if (!completedTrip) return
    setShowReturnOffer(false)

    setTripType('other')
    setSelectedAssignmentIds([])
    setStartForm({
      date: today(),
      from_address: completedTrip.to_address,
      to_address: completedTrip.from_address,
      odometer_start: completedTrip.odometer_end || 0,
      purpose: 'Returresa',
      notes: '',
    })
    setShowStartModal(true)
  }

  // ============================================
  // Edit / delete existing entries
  // ============================================

  const openEditModal = (entry: DrivingLogEntry) => {
    setEditingEntry(entry)
    setEditForm({
      date: entry.date,
      from_address: entry.from_address,
      to_address: entry.to_address,
      odometer_start: entry.odometer_start,
      odometer_end: entry.odometer_end,
      purpose: entry.purpose || '',
      notes: entry.notes || '',
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    if (!editingEntry) return

    if (editForm.odometer_end !== null && editForm.odometer_end < editForm.odometer_start) {
      toast.error('Mätare slut kan inte vara mindre än mätare start')
      return
    }

    try {
      await updateEntry.mutateAsync({
        id: editingEntry.id,
        vehicleId: editingEntry.vehicle_id,
        updates: {
          date: editForm.date,
          odometer_start: editForm.odometer_start,
          odometer_end: editForm.odometer_end,
          from_address: editForm.from_address.trim(),
          to_address: editForm.to_address.trim(),
          purpose: editForm.purpose?.trim() || null,
          notes: editForm.notes?.trim() || null,
        }
      })
      toast.success('Resa uppdaterad')
      setShowEditModal(false)
    } catch {
      toast.error('Kunde inte uppdatera resa')
    }
  }

  const handleDeleteEntry = async (entry: DrivingLogEntry) => {
    if (!confirm('Vill du ta bort denna resa?')) return
    try {
      await deleteEntry.mutateAsync({ id: entry.id, vehicleId: entry.vehicle_id })
      toast.success('Resa borttagen')
    } catch {
      toast.error('Kunde inte ta bort resa')
    }
  }

  // Odometer warning for start form
  const odometerDeviation = useMemo(() => {
    if (!lastEntry || !lastEntry.odometer_end) return null
    if (startForm.odometer_start === 0) return null
    const diff = startForm.odometer_start - lastEntry.odometer_end
    if (diff !== 0) {
      return { diff, lastEnd: lastEntry.odometer_end }
    }
    return null
  }, [lastEntry, startForm.odometer_start])

  // ============================================
  // Excel export
  // ============================================

  const handleExport = async () => {
    if (!selectedVehicleId) return
    setExporting(true)
    try {
      const data = await drivingLogEntriesService.getByVehicle(selectedVehicleId, exportFrom, exportTo)

      const vehicle = vehicles.find(v => v.id === selectedVehicleId)
      const vehicleName = vehicle ? `${vehicle.name} (${vehicle.registration_number})` : 'Fordon'

      const headers = ['Datum', 'Mätare start', 'Mätare slut', 'Från', 'Till', 'Ändamål', 'Km']
      const rows = data
        .sort((a, b) => a.date.localeCompare(b.date))
        .map(e => [
          e.date,
          e.odometer_start,
          e.odometer_end ?? '',
          e.from_address,
          e.to_address,
          e.purpose || '',
          e.km ?? '',
        ])

      const totalKmExport = data.reduce((sum, e) => sum + (e.km || 0), 0)
      rows.push(['', '', '', '', '', 'Totalt', totalKmExport])

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

      // Column widths
      ws['!cols'] = [
        { wch: 12 }, // Datum
        { wch: 14 }, // Mätare start
        { wch: 14 }, // Mätare slut
        { wch: 30 }, // Från
        { wch: 30 }, // Till
        { wch: 25 }, // Ändamål
        { wch: 8 },  // Km
      ]

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Körjournal')
      XLSX.writeFile(wb, `Korjournal_${vehicleName}_${exportFrom}_${exportTo}.xlsx`)

      toast.success(`Exporterade ${data.length} resor`)
      setShowExportModal(false)
    } catch {
      toast.error('Kunde inte exportera data')
    } finally {
      setExporting(false)
    }
  }

  if (loadingVehicles) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-nature-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Car className="h-6 w-6 text-nature-700 dark:text-nature-300" />
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Körjournal</h1>
        </div>
        {!openTrip ? (
          <button
            onClick={openStartModal}
            disabled={!selectedVehicleId}
            className="btn-primary flex items-center gap-2"
          >
            <Play className="h-4 w-4" />
            <span className="hidden sm:inline">Starta resa</span>
          </button>
        ) : (
          <button
            onClick={openFinishModal}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            <Flag className="h-4 w-4" />
            <span className="hidden sm:inline">Avsluta resa</span>
          </button>
        )}
      </div>

      {/* Open trip banner */}
      {openTrip && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm font-semibold text-green-800 dark:text-green-300">Pågående resa</span>
          </div>
          <div className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1">
            {openTrip.from_address}
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            {openTrip.to_address}
          </div>
          <div className="text-xs text-green-600 dark:text-green-500 mt-1">
            Mätare start: {openTrip.odometer_start} km
            {openTrip.purpose && ` · ${openTrip.purpose}`}
          </div>
        </div>
      )}

      {/* Vehicle selector */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl p-4 shadow-sm border border-nature-200 dark:border-nature-700">
        <div className="flex items-center gap-2 flex-wrap">
          {vehicles.map(v => (
            <button
              key={v.id}
              onClick={() => setSelectedVehicleId(v.id)}
              onDoubleClick={() => openVehicleModal(v.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedVehicleId === v.id
                  ? 'bg-nature-700 text-white dark:bg-nature-600'
                  : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-200 dark:hover:bg-nature-600'
              }`}
              title={`Dubbelklicka för att redigera – ${v.registration_number}`}
            >
              {v.name} ({v.registration_number})
            </button>
          ))}
          <button
            onClick={() => openVehicleModal()}
            className="px-3 py-2 rounded-lg text-sm font-medium text-nature-500 hover:text-nature-700 hover:bg-nature-100 dark:text-nature-400 dark:hover:text-nature-200 dark:hover:bg-nature-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Period filter */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl p-4 shadow-sm border border-nature-200 dark:border-nature-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
            className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: sv })}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setExportFrom(format(startOfMonth(currentMonth), 'yyyy-MM-dd'))
                setExportTo(format(endOfMonth(currentMonth), 'yyyy-MM-dd'))
                setShowExportModal(true)
              }}
              disabled={!selectedVehicleId}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-200"
              title="Exportera till Excel"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {selectedVehicleId && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl p-4 shadow-sm border border-nature-200 dark:border-nature-700">
            <div className="text-sm text-nature-500 dark:text-nature-400">Total km i perioden</div>
            <div className="text-2xl font-bold text-nature-900 dark:text-nature-100">{totalKm} km</div>
          </div>
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl p-4 shadow-sm border border-nature-200 dark:border-nature-700">
            <div className="text-sm text-nature-500 dark:text-nature-400">Senaste mätarställning</div>
            <div className="text-2xl font-bold text-nature-900 dark:text-nature-100">
              {lastEntry?.odometer_end ? `${lastEntry.odometer_end} km` : '–'}
            </div>
          </div>
        </div>
      )}

      {/* Entry list */}
      {selectedVehicleId && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700">
          {loadingEntries ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-nature-600" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-nature-500 dark:text-nature-400">
              Inga resor registrerade för denna period
            </div>
          ) : (
            <>
              {/* Mobile view */}
              <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
                {entries.map(entry => (
                  <div
                    key={entry.id}
                    className="p-4 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                        {entry.date}
                      </span>
                      {entry.odometer_end !== null ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {entry.km} km
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          Pågående
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-nature-600 dark:text-nature-300 mb-1 flex items-center gap-1">
                      {entry.from_address}
                      <ArrowRight className="h-3 w-3 flex-shrink-0" />
                      {entry.to_address}
                    </div>
                    {entry.purpose && (
                      <div className="text-xs text-nature-500 dark:text-nature-400">{entry.purpose}</div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => openEditModal(entry)}
                        className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(entry)}
                        className="p-1 text-nature-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop view */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-nature-200 dark:border-nature-700">
                      <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Datum</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Mätare start</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Mätare slut</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Från</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Till</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Ändamål</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Km</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-nature-100 dark:divide-nature-700">
                    {entries.map(entry => (
                      <tr key={entry.id} className={`hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors ${entry.odometer_end === null ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}>
                        <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">{entry.date}</td>
                        <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">{entry.odometer_start}</td>
                        <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">
                          {entry.odometer_end !== null ? entry.odometer_end : (
                            <span className="text-green-600 dark:text-green-400 italic">pågår</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 max-w-[200px] truncate">{entry.from_address}</td>
                        <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 max-w-[200px] truncate">{entry.to_address}</td>
                        <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 max-w-[200px] truncate">{entry.purpose || '–'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-nature-900 dark:text-nature-100 text-right">
                          {entry.km !== null ? entry.km : '–'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => openEditModal(entry)}
                              className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry)}
                              className="p-1 text-nature-400 hover:text-red-600 dark:hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* Vehicle modal */}
      {/* ============================================ */}
      {showVehicleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowVehicleModal(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {editingVehicleId ? 'Redigera fordon' : 'Nytt fordon'}
              </h3>
              <button onClick={() => setShowVehicleModal(false)} className="text-nature-400 hover:text-nature-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Namn</label>
                <input
                  type="text"
                  value={vehicleForm.name}
                  onChange={e => setVehicleForm(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                  placeholder="T.ex. Firmabilen"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Registreringsnummer</label>
                <input
                  type="text"
                  value={vehicleForm.registration_number}
                  onChange={e => setVehicleForm(prev => ({ ...prev, registration_number: e.target.value.toUpperCase() }))}
                  className="input-field uppercase"
                  placeholder="T.ex. ZDF537"
                />
              </div>
              {editingVehicleId && (
                <button
                  onClick={() => {
                    const v = vehicles.find(v => v.id === editingVehicleId)
                    if (v) { handleToggleVehicleActive(editingVehicleId, v.is_active); setShowVehicleModal(false) }
                  }}
                  className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400"
                >
                  {vehicles.find(v => v.id === editingVehicleId)?.is_active ? 'Inaktivera fordon' : 'Aktivera fordon'}
                </button>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowVehicleModal(false)} className="btn-secondary">Avbryt</button>
              <button onClick={handleSaveVehicle} disabled={createVehicle.isPending || updateVehicle.isPending} className="btn-primary">
                {(createVehicle.isPending || updateVehicle.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Spara
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Start trip modal */}
      {/* ============================================ */}
      {showStartModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowStartModal(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-lg w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Starta resa</h3>
              </div>
              <button onClick={() => setShowStartModal(false)} className="text-nature-400 hover:text-nature-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Datum</label>
                <input
                  type="date"
                  value={startForm.date}
                  max={today()}
                  onChange={e => setStartForm(prev => ({ ...prev, date: e.target.value }))}
                  className="input-field min-h-[48px]"
                />
                {startForm.date !== today() && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Registrerar för ett tidigare datum (missad resa)</p>
                )}
              </div>

              {/* Trip type */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Restyp</label>
                <div className="flex rounded-lg overflow-hidden border border-nature-200 dark:border-nature-600">
                  <button type="button" onClick={() => setTripType('assignment')}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${tripType === 'assignment' ? 'bg-nature-700 text-white dark:bg-nature-600' : 'bg-nature-50 text-nature-600 hover:bg-nature-100 dark:bg-nature-700 dark:text-nature-300'}`}>
                    Uppdrag
                  </button>
                  <button type="button" onClick={() => { setTripType('other'); setSelectedAssignmentIds([]) }}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${tripType === 'other' ? 'bg-nature-700 text-white dark:bg-nature-600' : 'bg-nature-50 text-nature-600 hover:bg-nature-100 dark:bg-nature-700 dark:text-nature-300'}`}>
                    Annan resa
                  </button>
                </div>
              </div>

              {/* Assignment selector */}
              {tripType === 'assignment' && (
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Uppdrag ({startForm.date})</label>
                  {assignmentsForDate.length === 0 ? (
                    <p className="text-sm text-nature-500 dark:text-nature-400 italic">Inga uppdrag hittade för detta datum</p>
                  ) : (
                    <div className="space-y-2">
                      {assignmentsForDate.map(a => (
                        <label key={a.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedAssignmentIds.includes(a.id)
                              ? 'border-nature-600 bg-nature-50 dark:border-nature-500 dark:bg-nature-700/50'
                              : 'border-nature-200 hover:border-nature-300 dark:border-nature-600 dark:hover:border-nature-500'
                          }`}>
                          <input type="checkbox" checked={selectedAssignmentIds.includes(a.id)} onChange={() => handleAssignmentToggle(a.id)}
                            className="mt-0.5 h-4 w-4 rounded border-nature-300 text-nature-600 focus:ring-nature-500" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-nature-900 dark:text-nature-100">{a.title}</span>
                              {a.start_date !== startForm.date && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{a.start_date}</span>
                              )}
                            </div>
                            <div className="text-xs text-nature-500 dark:text-nature-400 truncate">
                              {a.customer_name}{a.address ? ` – ${a.address}` : ''}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* From address */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Från</label>
                <input type="text" value={startForm.from_address}
                  onChange={e => setStartForm(prev => ({ ...prev, from_address: e.target.value }))}
                  className="input-field min-h-[48px]" placeholder="Startadress" />
                <AddressSearch onSelect={addr => {
                  const full = addr.city ? `${addr.street}, ${addr.city}` : addr.street
                  setStartForm(prev => ({ ...prev, from_address: full }))
                }} />
              </div>

              {/* To address */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Till</label>
                <input type="text" value={startForm.to_address}
                  onChange={e => setStartForm(prev => ({ ...prev, to_address: e.target.value }))}
                  className="input-field min-h-[48px]" placeholder="Destination" />
                {tripType === 'other' && (
                  <AddressSearch onSelect={addr => {
                    const full = addr.city ? `${addr.street}, ${addr.city}` : addr.street
                    setStartForm(prev => ({ ...prev, to_address: full }))
                  }} />
                )}
              </div>

              {/* Odometer start */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Mätarställning vid start
                </label>
                <input type="number" value={startForm.odometer_start || ''}
                  onChange={e => setStartForm(prev => ({ ...prev, odometer_start: Number(e.target.value) || 0 }))}
                  className="input-field min-h-[48px]" placeholder="Km" />
              </div>

              {/* Odometer warning */}
              {odometerDeviation && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Avvikelse: {odometerDeviation.diff > 0 ? '+' : ''}{odometerDeviation.diff} km
                    från senaste avläsning ({odometerDeviation.lastEnd} km)
                  </p>
                </div>
              )}

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Ändamål</label>
                <input type="text" value={startForm.purpose}
                  onChange={e => setStartForm(prev => ({ ...prev, purpose: e.target.value }))}
                  className="input-field min-h-[48px]"
                  placeholder={tripType === 'assignment' ? 'Fylls i automatiskt från uppdrag' : 'Beskriv syftet med resan'} />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Anteckningar (valfritt)</label>
                <textarea value={startForm.notes}
                  onChange={e => setStartForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="input-field min-h-[80px]" rows={2} placeholder="Frivilliga anteckningar..." />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowStartModal(false)} className="btn-secondary">Avbryt</button>
              <button onClick={handleStartTrip} disabled={createEntry.isPending}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors">
                {createEntry.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <Play className="h-4 w-4" />
                Starta resa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Finish trip modal */}
      {/* ============================================ */}
      {showFinishModal && openTrip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowFinishModal(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flag className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Avsluta resa</h3>
              </div>
              <button onClick={() => setShowFinishModal(false)} className="text-nature-400 hover:text-nature-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Trip summary */}
            <div className="mb-4 p-3 bg-nature-50 dark:bg-nature-700/50 rounded-lg text-sm">
              <div className="text-nature-600 dark:text-nature-300 flex items-center gap-1 mb-1">
                {openTrip.from_address} <ArrowRight className="h-3 w-3" /> {openTrip.to_address}
              </div>
              <div className="text-nature-500 dark:text-nature-400 text-xs">
                Mätare start: {openTrip.odometer_start} km
                {openTrip.purpose && ` · ${openTrip.purpose}`}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  <Square className="h-4 w-4 inline mr-1" />
                  Mätarställning vid ankomst
                </label>
                <input
                  type="number"
                  value={finishOdometer || ''}
                  onChange={e => setFinishOdometer(Number(e.target.value) || 0)}
                  className="input-field min-h-[48px] text-lg"
                  placeholder="Km"
                  autoFocus
                />
              </div>

              {finishOdometer > openTrip.odometer_start && (
                <div className="text-center text-lg font-bold text-nature-900 dark:text-nature-100">
                  Sträcka: {finishOdometer - openTrip.odometer_start} km
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowFinishModal(false)} className="btn-secondary">Avbryt</button>
              <button onClick={handleFinishTrip} disabled={updateEntry.isPending}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors">
                {updateEntry.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <Flag className="h-4 w-4" />
                Avsluta resa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Edit entry modal */}
      {/* ============================================ */}
      {showEditModal && editingEntry && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEditModal(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-lg w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Redigera resa</h3>
              <button onClick={() => setShowEditModal(false)} className="text-nature-400 hover:text-nature-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Datum</label>
                <input type="date" value={editForm.date} max={today()}
                  onChange={e => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                  className="input-field min-h-[48px]" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Från</label>
                  <input type="text" value={editForm.from_address}
                    onChange={e => setEditForm(prev => ({ ...prev, from_address: e.target.value }))}
                    className="input-field min-h-[48px]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Till</label>
                  <input type="text" value={editForm.to_address}
                    onChange={e => setEditForm(prev => ({ ...prev, to_address: e.target.value }))}
                    className="input-field min-h-[48px]" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Mätare start</label>
                  <input type="number" value={editForm.odometer_start || ''}
                    onChange={e => setEditForm(prev => ({ ...prev, odometer_start: Number(e.target.value) || 0 }))}
                    className="input-field min-h-[48px]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Mätare slut</label>
                  <input type="number" value={editForm.odometer_end ?? ''}
                    onChange={e => {
                      const val = e.target.value === '' ? null : Number(e.target.value) || 0
                      setEditForm(prev => ({ ...prev, odometer_end: val }))
                    }}
                    className="input-field min-h-[48px]" placeholder="Tom = pågående" />
                </div>
              </div>
              {editForm.odometer_end !== null && editForm.odometer_end > editForm.odometer_start && (
                <div className="text-sm font-medium text-nature-700 dark:text-nature-300">
                  Sträcka: {editForm.odometer_end - editForm.odometer_start} km
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Ändamål</label>
                <input type="text" value={editForm.purpose}
                  onChange={e => setEditForm(prev => ({ ...prev, purpose: e.target.value }))}
                  className="input-field min-h-[48px]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Anteckningar</label>
                <textarea value={editForm.notes}
                  onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="input-field min-h-[80px]" rows={2} />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowEditModal(false)} className="btn-secondary">Avbryt</button>
              <button onClick={handleSaveEdit} disabled={updateEntry.isPending} className="btn-primary">
                {updateEntry.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Spara
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Return trip offer */}
      {/* ============================================ */}
      {showReturnOffer && completedTrip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowReturnOffer(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ArrowRight className="h-5 w-5 text-nature-600 dark:text-nature-400 rotate-180" />
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Returresa?</h3>
            </div>
            <p className="text-sm text-nature-600 dark:text-nature-300 mb-4">
              Vill du starta en returresa från <strong>{completedTrip.to_address}</strong> till <strong>{completedTrip.from_address}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowReturnOffer(false)} className="btn-secondary">Nej tack</button>
              <button onClick={handleStartReturnTrip} className="btn-primary">Ja, starta</button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* Export modal */}
      {/* ============================================ */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowExportModal(false)} />
          <div className="relative bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Download className="h-5 w-5 text-nature-600 dark:text-nature-400" />
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Exportera till Excel</h3>
              </div>
              <button onClick={() => setShowExportModal(false)} className="text-nature-400 hover:text-nature-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Quick select buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => {
                  setExportFrom(format(startOfMonth(currentMonth), 'yyyy-MM-dd'))
                  setExportTo(format(endOfMonth(currentMonth), 'yyyy-MM-dd'))
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Vald månad
              </button>
              <button
                onClick={() => {
                  const prev = subMonths(currentMonth, 1)
                  setExportFrom(format(startOfMonth(prev), 'yyyy-MM-dd'))
                  setExportTo(format(endOfMonth(prev), 'yyyy-MM-dd'))
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Föregående månad
              </button>
              <button
                onClick={() => {
                  setExportFrom(format(startOfYear(currentMonth), 'yyyy-MM-dd'))
                  setExportTo(format(endOfYear(currentMonth), 'yyyy-MM-dd'))
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Hela året
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Från datum</label>
                  <input
                    type="date"
                    value={exportFrom}
                    onChange={e => setExportFrom(e.target.value)}
                    className="input-field min-h-[48px]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">Till datum</label>
                  <input
                    type="date"
                    value={exportTo}
                    onChange={e => setExportTo(e.target.value)}
                    className="input-field min-h-[48px]"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowExportModal(false)} className="btn-secondary">Avbryt</button>
              <button
                onClick={handleExport}
                disabled={exporting || !exportFrom || !exportTo}
                className="btn-primary flex items-center gap-2"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Exportera
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
